import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

interface PaginationState {
  page: number
  limit: number
  total: number
}

interface UsePaginationReturn {
  pagination: PaginationState
  searchTerm: string
  handlePageChange: (page: number) => void
  handleLimitChange: (limit: number) => void
  handleSearch: (searchTerm: string) => void
  handleResetSearch: () => void
  updateTotal: (total: number) => void
}

export const usePagination = (defaultLimit: number = 10): UsePaginationReturn => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize state from URL parameters
  const [pagination, setPagination] = useState<PaginationState>({
    page: parseInt(searchParams.get('page') || '1'),
    limit: parseInt(searchParams.get('limit') || defaultLimit.toString()),
    total: 0
  })
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')

  // Update URL when pagination state changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    params.set('page', pagination.page.toString())
    params.set('limit', pagination.limit.toString())
    if (searchTerm) {
      params.set('search', searchTerm)
    } else {
      params.delete('search')
    }

    setSearchParams(params)
  }, [pagination.page, pagination.limit, searchTerm, setSearchParams, searchParams])

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }))
  }

  const handleLimitChange = (newLimit: number) => {
    setPagination((prev) => ({
      ...prev,
      limit: newLimit,
      page: 1 // Reset to first page when changing limit
    }))
  }

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm)
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handleResetSearch = () => {
    setSearchTerm('')
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const updateTotal = (total: number) => {
    setPagination(prev => ({ ...prev, total }))
  }

  return {
    pagination,
    searchTerm,
    handlePageChange,
    handleLimitChange,
    handleSearch,
    handleResetSearch,
    updateTotal
  }
}
